import { DocumentType } from '@/types/schema';

import { DOCUMENT_TYPE_OPTIONS } from './documents.constants';

export function documentTypeIsRestricted(documentType: DocumentType | string) {
  const documentTypeOption = DOCUMENT_TYPE_OPTIONS.find((typeOption) => {
    return typeOption.value === (documentType as DocumentType);
  });
  return documentTypeOption?.disabled ?? false;
}

// Conservative canonicalization for dirty checks that preserves newlines
// but normalizes other whitespace artifacts from markdown transformation
// Basically: has any real content changes, we want to detect it as dirty.
export function canonicalizeSummaryForDirtyCheck(str: string): string {
  if (str == null) return '';

  // Normalize NBSPs to spaces
  let out = str.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');

  // Normalize line endings to LF
  out = out.replace(/\r\n?/g, '\n');

  // Canonicalize citations JSON payloads without altering surrounding text
  // Supports multiline citations with the 's' (dotAll) flag
  out = out.replace(/\[citations?=\[(.*?)\]]/gs, (_m, content: string) => {
    try {
      const parsed = JSON.parse(`[${content}]`);
      const normalized = JSON.stringify(parsed).slice(1, -1); // remove outer []
      return `[citations=[${normalized}]]`;
    } catch {
      return `[citations=[${content}]]`;
    }
  });

  // Trim trailing spaces at end of each line
  out = out
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/g, ''))
    .join('\n');

  // Drop trailing newlines at EOF (canonicalize to no trailing newline)
  out = out.replace(/\n+$/g, '');

  return out;
}

export function orderDocsAlphaByName<T extends { id: string; name: string }>(
  docs: T[]
): T[] {
  return docs.sort((a, b) => {
    const aName = a.name ?? '';
    const bName = b.name ?? '';
    return aName.localeCompare(bName);
  });
}

export function compareCreatedAtDescending(
  a: { createdAt: Date | string },
  b: { createdAt: Date | string }
): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

export const PRIMARY_CLIENT_PROFILE_DOCUMENTS = [
  DocumentType.Will,
  DocumentType.HealthcareProxy,
  DocumentType.PowerOfAttorney,
];
// Helper function to check if the document type requires client profile inputs on the Document Details Form
export function isClientProfileDocumentType(
  documentType: DocumentType | string
): boolean {
  const documentTypeOption = DOCUMENT_TYPE_OPTIONS.find((typeOption) => {
    return typeOption.value === (documentType as DocumentType);
  });

  // These additional types support individual associations without missing-document insights.
  return [
    DocumentType.AdvanceDirective,
    DocumentType.WillCodicil,
    DocumentType.LivingWill,
    ...PRIMARY_CLIENT_PROFILE_DOCUMENTS,
  ].includes(documentTypeOption?.value ?? DocumentType.Other);
}
